
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Partners() {
  const [selectedPartner, setSelectedPartner] = useState(0);

  const partners = [
    {
      name: 'Wonder Clean India',
      logo: '/images/partners/wonderclean-logo.jpg',
      description: 'Leading manufacturer of commercial washing machines and dryers with advanced technology and energy-efficient solutions.',
      specialties: ['Commercial Washing Machines', 'Industrial Dryers', 'Tunnel Washers', 'Barrier Washers'],
      founded: '1995',
      headquarters: 'Mumbai, India',
      certifications: ['ISO 9001:2015', 'CE Certified', 'Energy Star'],
      keyProducts: [
        'Front Load Washers (10-100 kg)',
        'Top Load Washers (15-50 kg)', 
        'Steam Heated Dryers',
        'Gas Heated Dryers'
      ],
      image: '/images/partners/wonderclean-facility.jpg'
    },
    {
      name: 'Electrolux Professional',
      logo: '/images/partners/electrolux-logo.jpg',
      description: 'Global leader in professional kitchen and laundry solutions, delivering innovative equipment for hospitality and healthcare sectors.',
      specialties: ['Professional Laundry', 'Kitchen Equipment', 'Dishwashing Systems', 'Waste Management'],
      founded: '1919',
      headquarters: 'Stockholm, Sweden',
      certifications: ['ISO 14001', 'ENERGY STAR', 'UL Listed'],
      keyProducts: [
        'Washer Extractors (6-55 kg)',
        'Tumble Dryers', 
        'Ironing Equipment',
        'Folding Machines'
      ],
      image: '/images/partners/electrolux-facility.jpg'
    },
    {
      name: 'Stefab India',
      logo: '/images/partners/stefab-logo.jpg',
      description: 'Innovative manufacturer specializing in custom laundry solutions and automated systems for large-scale operations.',
      specialties: ['Automated Systems', 'Custom Solutions', 'Conveyor Systems', 'Sorting Equipment'],
      founded: '2008',
      headquarters: 'Bangalore, India',
      certifications: ['ISO 9001:2015', 'Make in India', 'BIS Certified'],
      keyProducts: [
        'Automated Sorting Systems',
        'Conveyor Belts', 
        'Washing Line Integration',
        'Custom Laundry Solutions'
      ],
      image: '/images/partners/stefab-facility.jpg'
    },
    {
      name: 'Alliance Laundry Systems',
      logo: '/images/partners/alliance-logo.jpg',
      description: 'American manufacturer of commercial laundry equipment, known for durability and reliability in demanding environments.',
      specialties: ['Speed Queen Equipment', 'Coin-Op Laundry', 'Multi-Housing', 'Route Operated'],
      founded: '1908',
      headquarters: 'Wisconsin, USA',
      certifications: ['UL Listed', 'Energy Star', 'NSF Certified'],
      keyProducts: [
        'Speed Queen Washers',
        'Stack Dryers', 
        'Coin-Operated Equipment',
        'Card-Operated Systems'
      ],
      image: '/images/partners/alliance-facility.jpg'
    }
  ];

  const partnershipBenefits = [
    {
      icon: 'ri-award-line',
      title: 'Authorized Dealer',
      description: 'Official authorized dealer status with direct manufacturer support and genuine parts supply.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Technical Training',
      description: 'Regular technical training programs for our team to ensure expert installation and service.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Warranty Support',
      description: 'Comprehensive warranty coverage and manufacturer-backed service guarantees for all equipment.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Logistics Support',
      description: 'Streamlined supply chain and logistics support ensuring timely delivery of equipment.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Technical Support',
      description: '24/7 technical support and troubleshooting assistance from manufacturer experts.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Competitive Pricing',
      description: 'Best-in-market pricing through direct partnerships and volume purchase agreements.'
    }
  ];

  const partnershipStats = [
    { label: 'Trusted Partners', value: '4+', icon: 'ri-handshake-line' },
    { label: 'Years of Partnership', value: '5+', icon: 'ri-time-line' },
    { label: 'Equipment Models', value: '50+', icon: 'ri-settings-4-line' },
    { label: 'Successful Projects', value: '500+', icon: 'ri-trophy-line' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section - Recreated without gradients */}
      <section className="pt-32 pb-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-5"
            style={{
              backgroundImage: `url('/images/backgrounds/partners-hero-bg.jpg')`
            }}
          ></div>
        </div>

        {/* Clean Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-32 right-20 w-12 h-12 bg-teal-100 rounded-full opacity-25 animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-10 h-10 bg-gray-100 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-blue-100 rounded-full opacity-30 animate-bounce" style={{ animationDelay: '2s' }}></div>

        {/* Simple Icon Elements */}
        <div className="absolute top-40 left-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float">
          <i className="ri-handshake-line text-blue-500 text-lg"></i>
        </div>
        <div className="absolute bottom-32 right-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float" style={{ animationDelay: '1.5s' }}>
          <i className="ri-building-line text-teal-500 text-lg"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm font-semibold shadow-sm">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-handshake-line text-sm text-white"></i>
                </div>
                Trusted Manufacturing Partners
                <span className="w-2 h-2 bg-blue-500 rounded-full ml-3"></span>
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
              Our{' '}
              <span className="text-blue-600">
                Trusted Partners
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Strong partnerships with world-class manufacturers enable us to deliver 
              the best commercial laundry solutions to our customers across India.
            </p>
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(5deg);
            }
          }
          .animate-float {
            animation: animate-float 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Partnership Stats */}
      <section className="py-8 bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {partnershipStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-xl text-white`}></i>
                </div>
                <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Navigation */}
      <section className="py-8 bg-white shadow-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {partners.map((partner, index) => (
              <div key={index} className="group/btn relative">
                {/* 3D Button Effect */}
                <div className={`absolute inset-0 ${selectedPartner === index ? 'bg-blue-700' : 'bg-gray-300'} rounded-full transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300`}></div>
                <button
                  onClick={() => setSelectedPartner(index)}
                  className={`relative ${
                    selectedPartner === index
                      ? 'bg-blue-600 text-white border-white/20'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  } font-medium px-6 py-3 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl whitespace-nowrap border-2`}
                >
                  {partner.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Partner Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <img 
                  src={partners[selectedPartner].logo}
                  alt={`${partners[selectedPartner].name} Logo`}
                  className="h-16 mb-4"
                />
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  {partners[selectedPartner].name}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {partners[selectedPartner].description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Founded</h4>
                  <p className="text-gray-600">{partners[selectedPartner].founded}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Headquarters</h4>
                  <p className="text-gray-600">{partners[selectedPartner].headquarters}</p>
                </div>
              </div>

              <div className="mb-8">
                <h4 className="font-semibold text-gray-800 mb-3">Certifications</h4>
                <div className="flex flex-wrap gap-2">
                  {partners[selectedPartner].certifications.map((cert, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm border border-green-200"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <img 
                src={partners[selectedPartner].image}
                alt={partners[selectedPartner].name}
                className="rounded-2xl shadow-xl w-full border border-gray-100"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Specialties</h4>
              <ul className="space-y-2">
                {partners[selectedPartner].specialties.map((specialty, index) => (
                  <li key={index} className="flex items-center text-gray-600">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    {specialty}
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Key Products</h4>
              <ul className="space-y-2">
                {partners[selectedPartner].keyProducts.map((product, index) => (
                  <li key={index} className="flex items-center text-gray-600">
                    <i className="ri-settings-4-line text-blue-500 mr-2"></i>
                    {product}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        {/* Professional Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-teal-200/25 rounded-full blur-2xl animate-bounce"></div>
          <div className="absolute bottom-32 left-1/4 w-28 h-28 bg-purple-200/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 right-20 w-20 h-20 bg-blue-300/30 rounded-full blur-xl animate-bounce" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Floating 3D Icons */}
        <div className="absolute top-1/4 left-16 w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-2xl border border-white/20">
          <i className="ri-handshake-line text-blue-500/70 text-2xl"></i>
        </div>
        <div className="absolute top-1/2 right-20 w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-2xl border border-white/20" style={{ animationDelay: '1.5s' }}>
          <i className="ri-award-line text-teal-500/70 text-2xl"></i>
        </div>
        <div className="absolute bottom-1/3 left-1/2 w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-2xl border border-white/20" style={{ animationDelay: '3s' }}>
          <i className="ri-shield-check-line text-purple-500/70 text-2xl"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Enhanced Section Header */}
          <div className="text-center mb-20">
            <div className="mb-8">
              <span className="inline-flex items-center px-8 py-4 rounded-full bg-white/20 backdrop-blur-2xl border border-white/30 text-blue-800 text-sm font-bold shadow-2xl">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-4 shadow-lg">
                  <i className="ri-award-line text-sm text-white"></i>
                </div>
                Partnership Advantages
                <span className="w-3 h-3 bg-blue-500 rounded-full ml-4 animate-pulse"></span>
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-8 leading-tight">
              Partnership Benefits
            </h2>
            <div className="flex justify-center mb-6">
              <div className="w-32 h-1 bg-blue-500 rounded-full"></div>
            </div>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-medium">
              How our strong manufacturer partnerships benefit our customers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="p-8 h-full bg-white/15 backdrop-blur-2xl border border-white/30 shadow-2xl hover:shadow-3xl hover:-translate-y-3 hover:bg-white/25 transition-all duration-500 relative overflow-hidden transform hover:scale-105 rounded-3xl">
                  {/* Subtle Card Background Glow */}
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>

                  {/* Floating Particle Effects */}
                  <div className="absolute top-4 right-4 w-3 h-3 bg-blue-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute bottom-4 left-4 w-2 h-2 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.5s' }}></div>

                  {/* 3D Icon Container */}
                  <div className="relative mb-8 flex justify-center">
                    <div className="w-20 h-20 bg-blue-500 rounded-3xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-blue-600 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                      <i className={`${benefit.icon} text-white text-3xl group-hover:scale-110 transition-transform duration-300`}></i>

                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-blue-400/50 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                    </div>

                    {/* Floating Ring */}
                    <div className="absolute inset-0 w-20 h-20 border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100 mx-auto"></div>
                  </div>

                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-700 transition-colors duration-300">
                      {benefit.title}
                    </h3>

                    {/* Animated Underline */}
                    <div className="w-16 h-1 bg-blue-500 rounded-full mb-6 group-hover:w-24 transition-all duration-500 mx-auto"></div>

                    <p className="text-gray-600 leading-relaxed text-lg group-hover:text-gray-700 transition-colors duration-300">
                      {benefit.description}
                    </p>

                    {/* Interactive Progress Bar */}
                    <div className="mt-8 h-2 bg-gray-200/50 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-6 right-6 w-10 h-10 bg-blue-500/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Reflection Effect */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-3xl"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Professional Contact Information Section */}
          <div className="mt-20">
            <div className="bg-white/15 backdrop-blur-2xl border border-white/30 shadow-2xl rounded-3xl p-12 relative overflow-hidden">
              {/* Background Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-28 h-28 bg-teal-400/10 rounded-full blur-2xl"></div>

              <div className="relative z-10">
                <div className="text-center mb-12">
                  <h3 className="text-4xl font-bold text-gray-800 mb-6">
                    Ready to Experience These Benefits?
                  </h3>
                  <div className="flex justify-center mb-6">
                    <div className="w-24 h-1 bg-blue-500 rounded-full"></div>
                  </div>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                    Connect with our partnership team to learn how these manufacturer relationships can benefit your business operations.
                  </p>
                </div>

                {/* 3D Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
                  <div className="group cursor-pointer">
                    <div className="bg-white/20 backdrop-blur-2xl border border-white/30 rounded-2xl p-8 text-center shadow-2xl hover:shadow-3xl hover:-translate-y-2 hover:scale-105 transition-all duration-500 relative overflow-hidden">
                      <div className="absolute inset-0 bg-white/10 rounded-2xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
                      <div className="text-4xl font-bold text-blue-600 mb-3 group-hover:scale-110 transition-transform duration-300">98%</div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Customer Satisfaction</div>
                    </div>
                  </div>
                  <div className="group cursor-pointer">
                    <div className="bg-white/20 backdrop-blur-2xl border border-white/30 rounded-2xl p-8 text-center shadow-2xl hover:shadow-3xl hover:-translate-y-2 hover:scale-105 transition-all duration-500 relative overflow-hidden">
                      <div className="absolute inset-0 bg-white/10 rounded-2xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
                      <div className="text-4xl font-bold text-teal-600 mb-3 group-hover:scale-110 transition-transform duration-300">24/7</div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Technical Support</div>
                    </div>
                  </div>
                  <div className="group cursor-pointer">
                    <div className="bg-white/20 backdrop-blur-2xl border border-white/30 rounded-2xl p-8 text-center shadow-2xl hover:shadow-3xl hover:-translate-y-2 hover:scale-105 transition-all duration-500 relative overflow-hidden">
                      <div className="absolute inset-0 bg-white/10 rounded-2xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
                      <div className="text-4xl font-bold text-purple-600 mb-3 group-hover:scale-110 transition-transform duration-300">5+</div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Years Experience</div>
                    </div>
                  </div>
                </div>

                {/* Professional Contact Buttons */}
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link to="/contact">
                    <div className="group/btn relative">
                      {/* 3D Button Effect */}
                      <div className="absolute inset-0 bg-blue-700 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
                      <button className="relative bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-5 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                        <span className="flex items-center text-lg">
                          <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                            <i className="ri-phone-line text-sm"></i>
                          </div>
                          Schedule Partnership Meeting
                        </span>
                      </button>
                    </div>
                  </Link>
                  <Link to="/services">
                    <div className="group/btn relative">
                      {/* 3D Button Effect */}
                      <div className="absolute inset-0 bg-gray-300 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
                      <button className="relative bg-white/80 backdrop-blur-2xl border-2 border-gray-300 hover:bg-white hover:border-gray-400 text-gray-800 hover:text-gray-900 font-bold px-10 py-5 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap">
                        <span className="flex items-center text-lg">
                          <div className="w-8 h-8 bg-gray-200/60 rounded-xl flex items-center justify-center mr-4">
                            <i className="ri-information-line text-sm"></i>
                          </div>
                          Learn More About Benefits
                        </span>
                      </button>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Floating Animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
              opacity: 0.7;
            }
            25% {
              transform: translateY(-12px) rotate(3deg);
              opacity: 1;
            }
            50% {
              transform: translateY(-20px) rotate(-2deg);
              opacity: 0.8;
            }
            75% {
              transform: translateY(-8px) rotate(4deg);
              opacity: 1;
            }
          }
          .animate-float {
            animation: animate-float 4s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Why Choose Our Partnerships */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/images/partners/partnership-handshake.jpg"
                alt="Partnership Success"
                className="rounded-2xl shadow-xl border border-gray-100"
              />
            </div>
            
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Our Partnerships Matter</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1 border border-blue-200">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Direct Access to Innovation</h4>
                    <p className="text-gray-600">Get first access to the latest technology and equipment innovations from our manufacturing partners.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1 border border-blue-200">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Guaranteed Authenticity</h4>
                    <p className="text-gray-600">All equipment comes with manufacturer warranties and genuine parts support.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1 border border-blue-200">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Expert Technical Support</h4>
                    <p className="text-gray-600">Benefit from manufacturer-trained technicians and comprehensive support programs.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1 border border-blue-200">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Competitive Pricing</h4>
                    <p className="text-gray-600">Enjoy competitive pricing through our strong relationships and bulk purchasing power.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Work with Trusted Partners?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Leverage our strong manufacturer partnerships to get the best equipment, 
            pricing, and support for your commercial laundry needs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <div className="group/btn relative">
                {/* 3D Button Effect */}
                <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                <button className="relative bg-white text-blue-600 hover:bg-gray-50 font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap border-2 border-white/20">
                  <span className="flex items-center">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-phone-line text-sm text-blue-600"></i>
                    </div>
                    Get Equipment Quote
                  </span>
                </button>
              </div>
            </Link>
            <Link to="/services">
              <div className="group/btn relative">
                {/* 3D Button Effect */}
                <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                <button className="relative bg-white/10 backdrop-blur-xl border-2 border-white/30 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap">
                  <span className="flex items-center">
                    <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-service-line text-sm"></i>
                    </div>
                    View Our Services
                  </span>
                </button>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
